import { useTranslation } from "next-i18next/pages";

// custom
import { MeFieldFragment } from "graphql/generated";
import { useMe } from "lib/auth";
import { BaseLink } from "components/atoms/NitoelLink";
import DotMenu, { DotMenuProps } from "components/atoms/DotMenu";
import auth from "lib/auth";

// mui
import { LinkProps } from "@mui/material/Link";
import Button, { ButtonProps } from "@mui/material/Button";
import IconButton, { IconButtonProps } from "@mui/material/IconButton";
import MenuItem, { MenuItemProps } from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { ReactNode } from "react";

const allPermissions = ["read", "write", "apply", "export", "quotation_apply", "quotation_proceed"] as const;
export type permissionValues = (typeof allPermissions)[number];

type permissionPropsType = {
  resource: string; // resourceももう少し制御するかも
  permission: permissionValues;
  tooltip?: string;
  children?: React.ReactNode;
};

// セットでよく使うので、lib/authのcanをそのままimport
export const can = (me: MeFieldFragment | null, resource: string, permission: permissionValues): boolean => {
  return auth.can(me, resource, permission);
};

// variant="contained"をよく使うのでそれをdefaultで渡してる。
// もし上書きしたい場合はpropsで渡せばうわがけるのでそうしてください。
export const PermittableButton = (props: permissionPropsType & ButtonProps): React.JSX.Element => {
  const { resource, permission, tooltip, ...rest } = props;
  const me = useMe();

  if (can(me, resource, permission)) {
    if (tooltip) {
      return (
        <Tooltip title={tooltip}>
          <Button variant="contained" {...rest}>
            {rest.children}
          </Button>
        </Tooltip>
      );
    }

    return (
      <Button variant="contained" {...rest}>
        {rest.children}
      </Button>
    );
  }

  return <></>;
};

// 三点リーダーの中の操作メニューで権限管理したいものに使う。権限ない際に非表示なのは上と同じ。
export const PermittableMenuItem = (props: permissionPropsType & MenuItemProps): React.JSX.Element => {
  const { resource, permission, ...rest } = props;
  const me = useMe();

  if (can(me, resource, permission)) {
    return <MenuItem {...rest}>{rest.children}</MenuItem>;
  }

  return <></>;
};

export const PermittableLink = (props: permissionPropsType & LinkProps): React.JSX.Element => {
  const { resource, permission, children, ...others } = props;
  const me = useMe();

  if (can(me, resource, permission)) {
    return <BaseLink {...others}>{children}</BaseLink>;
  }

  return <></>;
};

export const PermittableIconButton = (props: permissionPropsType & IconButtonProps): React.JSX.Element => {
  const { resource, permission, tooltip, ...rest } = props;
  const me = useMe();

  if (can(me, resource, permission)) {
    if (tooltip) {
      return (
        <Tooltip title={tooltip}>
          <IconButton {...rest}>{rest.children}</IconButton>
        </Tooltip>
      );
    }

    return <IconButton {...rest}>{rest.children}</IconButton>;
  }

  return <></>;
};

export const PermittableDotMenu = (props: permissionPropsType & DotMenuProps): React.JSX.Element => {
  const { resource, permission, ...rest } = props;

  const me = useMe();

  if (can(me, resource, permission)) {
    return <DotMenu {...rest} />;
  }

  return <></>;
};

//// 下は主にCSRな画面制御で利用想定されるメソッド群 ////
// CSRの画面でfetchした時のerrorメッセージでpermissionを判定するためのもの
export const permissionDenied = (error: Error | null | undefined): boolean => {
  if (!error) return false;

  return error.message.startsWith("[GraphQL] permissionDeny");
};

export const PermissionDenied = (): React.JSX.Element => {
  const { t } = useTranslation(["common"]);

  return (
    <>
      <Typography variant="h6">403 PermissionDenied</Typography>
      <Typography variant="body1">{t("common:permission_denied")}</Typography>
    </>
  );
};

// 複数の権限を持っているかどうかを判定して子要素を表示する
// andとorの判定ができる
type permissionCheckType = "and" | "or";
export type multiplePermittableAreaProp = {
  permissions: permissionPropsType[];
  checkType: permissionCheckType;
  children: ReactNode;
};
export const MultiplePermittableArea = ({ permissions, checkType, children }: multiplePermittableAreaProp): React.JSX.Element => {
  const me = useMe();
  let isAuthorized: boolean;

  if (checkType === "and") {
    isAuthorized = permissions.every((p) => can(me, p.resource, p.permission));
  } else {
    isAuthorized = permissions.some((p) => can(me, p.resource, p.permission));
  }
  return <>{isAuthorized && children}</>;
};
